/* ============================================================
   Blog.js — Index du blog (route /blog)
   ============================================================
   - Liste les articles depuis data/blogPosts.js
   - SEO : <title>, meta description, OG, canonical
   - Schema.org JSON-LD : Blog + ItemList
   - Si aucun article : affiche le state "coming soon"
   ============================================================ */
import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuroraBackground from '../components/AuroraBackground/AuroraBackground';
import AuroraNavbar from '../components/AuroraNavbar/AuroraNavbar';
import Footer from '../components/Footer';
import MetaHead from '../components/MetaHead';
import { getAllPosts } from '../data/blogPosts';

// SEO Blog — STRICTEMENT INCHANGÉ (référencement critique sur cette URL en FR).
// Les meta multilingues sont gérées par <MetaHead page="blog" /> en complément
// pour les variantes ?lang=. Le JSON-LD Blog reste FR car les articles le sont.
const SITE_URL = 'https://www.getrevyo.com';
const PAGE_TITLE = 'Blog Revyo — Conseils SEO & avis Google pour restaurants';
const PAGE_DESC  = "Conseils, études et retours terrain pour augmenter la note Google de votre restaurant et collecter plus d'avis positifs.";

function upsertMeta(attr, name, content) {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function Blog() {
  const navigate = useNavigate();
  const posts = getAllPosts();
  const { t, i18n } = useTranslation('blog');

  /* SEO — STRICTEMENT INCHANGÉ (référencement critique) */
  useEffect(() => {
    document.title = PAGE_TITLE;
    upsertMeta('name', 'description', PAGE_DESC);
    upsertMeta('property', 'og:title', PAGE_TITLE);
    upsertMeta('property', 'og:description', PAGE_DESC);
    upsertMeta('property', 'og:url', `${SITE_URL}/blog`);
    upsertMeta('property', 'og:type', 'website');
    upsertLink('canonical', `${SITE_URL}/blog`);

    // JSON-LD Blog + ItemList
    upsertJsonLd('ld-blog', {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Le Journal Revyo',
      description: PAGE_DESC,
      url: `${SITE_URL}/blog`,
      publisher: {
        '@type': 'Organization',
        name: 'Revyo',
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/web-app-manifest-512x512.png` }
      },
      blogPost: posts.map(p => ({
        '@type': 'BlogPosting',
        headline: p.title,
        url: `${SITE_URL}/blog/${p.slug}`,
        datePublished: p.publishDate,
        description: p.metaDescription
      }))
    });

    return () => {
      const el = document.getElementById('ld-blog');
      if (el) el.remove();
    };
  }, [posts]);

  const goRegister = () => navigate('/register');

  // Date formatée selon la langue active (fr-FR / en-GB / es-ES / it-IT)
  const localeMap = { fr: 'fr-FR', en: 'en-GB', es: 'es-ES', it: 'it-IT' };
  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString(localeMap[i18n.language] || 'fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <>
      {/* MetaHead branche les meta multilingues seo.blog sur les variantes ?lang=
          en complément du SEO FR principal (PAGE_TITLE) plus haut. */}
      <MetaHead page="blog" />
      <AuroraBackground dim />
      <AuroraNavbar activeLink="blog" />

      <style>{blogCss}</style>
      <div className="blog-page-wrap">

        {/* HERO */}
        <section className="blog-hero">
          <div className="blog-hero-inner">
            <h1 className="blog-h1">{t('hero.title')}</h1>
            <p className="blog-sub">{t('hero.subtitle')}</p>
          </div>
        </section>

        {/* === LISTE D'ARTICLES === */}
        {/* NB P3 : les articles eux-mêmes (title, excerpt, category) restent en FR
            volontairement — un article = un travail de copywriting par langue
            (planifié en P3b). En attendant, la shell (CTA "Lire l'article",
            coming-soon, hero) est i18n-isée. */}
        {posts.length > 0 ? (
          <section className="blog-list">
            <div className="blog-list-inner">
              {posts.map((post, idx) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className={'blog-card' + (idx === 0 ? ' blog-card-feat' : '')}
                >
                  <div className="blog-card-top">
                    <span className="blog-tag">{post.category}</span>
                    <span className="blog-meta-sep">·</span>
                    <span className="blog-meta">{formatDate(post.publishDate)}</span>
                    <span className="blog-meta-sep">·</span>
                    <span className="blog-meta">{post.readTime}</span>
                  </div>
                  <h2 className="blog-card-h2">{post.title}</h2>
                  <p className="blog-card-excerpt">{post.excerpt}</p>
                  <span className="blog-card-link">
                    {t('card.read_article')}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                  </span>
                </Link>
              ))}
            </div>
          </section>
        ) : (
          /* === COMING SOON (fallback s'il n'y a pas d'articles) === */
          <section className="blog-coming">
            <div className="blog-coming-card">
              <div className="blog-coming-ic">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M19 4H5a2 2 0 0 0-2 2v14l4-4h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" /><line x1="8" y1="10" x2="16" y2="10" /><line x1="8" y1="14" x2="13" y2="14" /></svg>
              </div>
              <h2 className="blog-coming-h2">{t('coming_soon.title')}</h2>
              <p className="blog-coming-text">
                {t('coming_soon.text')}
              </p>
              <button className="blog-coming-cta" onClick={goRegister}>
                {t('coming_soon.cta')}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
              </button>
            </div>
          </section>
        )}

      </div>
      <Footer />
    </>
  );
}

const blogCss = `
/* Wrapper contenu — critique : position:relative + z-index pour passer au-dessus de aurora-bg */
.blog-page-wrap {
  position: relative;
  z-index: 1;
  padding-top: 88px;
  min-height: calc(100vh - 88px);
}

/* === HERO === */
.blog-hero {
  padding: 60px 28px 40px;
  text-align: center;
  max-width: 760px;
  margin: 0 auto;
}
.blog-hero-inner { max-width: 720px; margin: 0 auto; }
.blog-h1 {
  font-family: 'Sora', 'Inter', sans-serif;
  font-size: clamp(40px, 6vw, 64px);
  font-weight: 700;
  letter-spacing: -.025em;
  line-height: 1.05;
  color: #F5F2EB;
  text-wrap: balance;
  margin: 0;
}
.blog-sub {
  font-size: 19px;
  line-height: 1.55;
  color: rgba(245,242,235,.62);
  margin: 22px auto 0;
  max-width: 560px;
  text-wrap: balance;
}

/* === LISTE D'ARTICLES === */
.blog-list { padding: 40px 28px 120px; }
.blog-list-inner {
  max-width: 920px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.blog-card {
  background: rgba(255,255,255,.025);
  border: 1px solid rgba(237,234,227,.08);
  border-radius: 22px;
  padding: 32px 36px;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  text-decoration: none;
  color: #F5F2EB;
  display: flex;
  flex-direction: column;
  gap: 14px;
  transition: transform .25s cubic-bezier(.2,.8,.2,1), border-color .25s, box-shadow .25s;
}
.blog-card:hover {
  transform: translateY(-3px);
  border-color: rgba(232,80,42,.4);
  box-shadow: 0 24px 48px -20px rgba(232,80,42,.25);
}
.blog-card-feat {
  padding: 44px 44px;
  background: linear-gradient(160deg, rgba(232,80,42,.08) 0%, rgba(255,255,255,.025) 100%);
  border-color: rgba(232,80,42,.25);
}

.blog-card-top {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.blog-tag {
  background: rgba(232,80,42,.15);
  color: #FF8A3D;
  padding: 5px 12px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
}
.blog-meta-sep { color: rgba(245,242,235,.3); }
.blog-meta {
  font-size: 13px;
  color: rgba(245,242,235,.62);
}

.blog-card-h2 {
  font-family: 'Sora', 'Inter', sans-serif;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -.025em;
  line-height: 1.2;
  color: #F5F2EB;
  margin: 0;
  text-wrap: balance;
}
.blog-card-feat .blog-card-h2 {
  font-size: 32px;
  letter-spacing: -.03em;
}

.blog-card-excerpt {
  font-size: 16px;
  color: rgba(245,242,235,.7);
  line-height: 1.6;
  margin: 0;
  text-wrap: pretty;
}
.blog-card-feat .blog-card-excerpt { font-size: 17px; }

.blog-card-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #E8502A;
  margin-top: 2px;
}
.blog-card-link svg { transition: transform .15s ease; }
.blog-card:hover .blog-card-link svg { transform: translateX(3px); }
.blog-card:hover .blog-card-link { color: #FF8A3D; }

/* === COMING SOON === */
.blog-coming {
  padding: 60px 28px 120px;
}
.blog-coming-card {
  max-width: 720px;
  margin: 0 auto;
  background: rgba(255,255,255,.025);
  border: 1px solid rgba(237,234,227,.08);
  border-radius: 24px;
  padding: 64px 48px;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  text-align: center;
}
.blog-coming-ic {
  width: 68px;
  height: 68px;
  border-radius: 18px;
  background: rgba(232,80,42,.15);
  border: 1px solid rgba(232,80,42,.25);
  color: #E8502A;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 28px;
}
.blog-coming-h2 {
  font-family: 'Sora', 'Inter', sans-serif;
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -.025em;
  line-height: 1.15;
  color: #F5F2EB;
  text-wrap: balance;
  margin: 0;
}
.blog-coming-text {
  font-size: 17px;
  color: rgba(245,242,235,.7);
  line-height: 1.6;
  margin-top: 20px;
  text-wrap: pretty;
}
.blog-coming-cta {
  margin-top: 36px;
  background: linear-gradient(135deg, #E8502A 0%, #FF6B3D 100%);
  color: white;
  padding: 15px 26px;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 16px 40px -10px rgba(232,80,42,.6), inset 0 1px 0 rgba(255,255,255,.25);
  transition: transform .2s, box-shadow .2s;
}
.blog-coming-cta:hover {
  transform: translateY(-1px);
  box-shadow: 0 20px 50px -10px rgba(232,80,42,.75);
}

/* === RESPONSIVE === */
@media (max-width: 900px) {
  .blog-hero { padding: 40px 20px 30px; }
  .blog-list { padding: 30px 20px 90px; }
  .blog-card { padding: 24px 22px; border-radius: 18px; }
  .blog-card-feat { padding: 28px 24px; }
  .blog-card-h2 { font-size: 21px; letter-spacing: -.02em; }
  .blog-card-feat .blog-card-h2 { font-size: 24px; }
  .blog-card-excerpt { font-size: 15px; }
  .blog-coming { padding: 40px 20px 90px; }
  .blog-coming-card { padding: 44px 28px; border-radius: 20px; }
  .blog-coming-h2 { font-size: 24px; letter-spacing: -.02em; }
  .blog-coming-text { font-size: 16px; }
}
`;
